import { useId } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { useCompanies } from '../../hooks/useCompanies';
import { useI18n } from '../../contexts/I18nContext';
import { Popover } from '../tasks/linear/Popover';

/**
 * Firm multi-select for the Financials page (multi-company). Unlike the
 * single-select global switcher, this filters aggregations across any subset
 * of firms: `value` is the selected firm ids, `[]` = all firms.
 *
 * Renders nothing with ≤1 firm (single-firm installs stay visually identical).
 */
export function CompanyMultiSelect({
  value,
  onChange,
  className,
}: {
  value: string[];
  onChange: (ids: string[]) => void;
  className?: string;
}) {
  const { t } = useI18n();
  const { data: companies } = useCompanies();
  const labelId = `${useId()}-label`;

  if (!companies || companies.length <= 1) return null;

  // Drop stale ids (deleted firms) from the summary logic.
  const selected = value.filter((id) => companies.some((c) => c.id === id));
  const allSelected = selected.length === 0;

  const summary = allSelected
    ? t('financials.companyFilter.all')
    : selected.length === 1
      ? (() => {
          const c = companies.find((x) => x.id === selected[0]);
          return c ? c.displayName || c.legalName : t('financials.companyFilter.all');
        })()
      : t('financials.companyFilter.selected', { count: selected.length });

  function toggleCompany(id: string) {
    if (selected.includes(id)) {
      onChange(selected.filter((x) => x !== id));
    } else {
      const next = [...selected, id];
      // Selecting every firm is the same as "all" — collapse back to [].
      onChange(next.length === companies!.length ? [] : next);
    }
  }

  return (
    <div className={className}>
      <span id={labelId} className="ui-field-label">
        {t('financials.companyFilter.label')}
      </span>
      <Popover
        width={260}
        matchTriggerWidth
        trigger={({ open, toggle, ref }) => (
          <button
            type="button"
            ref={(el) => ref(el)}
            onClick={toggle}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-labelledby={labelId}
            className="ui-select-trigger"
          >
            <span className="truncate">{summary}</span>
            <ChevronDown size={14} className="ml-auto shrink-0 opacity-60" />
          </button>
        )}
      >
        {() => (
          <div className="max-h-72 overflow-y-auto py-1" role="listbox" aria-multiselectable>
            <button
              type="button"
              role="option"
              aria-selected={allSelected}
              onClick={() => onChange([])}
              className="ui-menu-item justify-between"
            >
              <span className="truncate">{t('financials.companyFilter.all')}</span>
              {allSelected && <Check size={14} className="shrink-0 text-gray-400" />}
            </button>
            <div className="my-1 border-t border-gray-200" />
            {companies.map((company) => {
              const isOn = selected.includes(company.id);
              return (
                <button
                  key={company.id}
                  type="button"
                  role="option"
                  aria-selected={isOn}
                  onClick={() => toggleCompany(company.id)}
                  className="ui-menu-item justify-between"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <span
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                        isOn ? 'border-brand-600 bg-brand-600 text-white' : 'border-gray-300'
                      }`}
                    >
                      {isOn && <Check size={12} />}
                    </span>
                    <span className="truncate">{company.displayName || company.legalName}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </Popover>
    </div>
  );
}
